import { useState } from 'react';
import type { ReactElement } from 'react';
import { Header } from './shell/Header.js';
import { SideMenu } from './shell/SideMenu.js';
import { Dashboard } from './views/Dashboard.js';
import { Calendar } from './views/Calendar.js';
import { Planner } from './views/Planner.js';
import { Recipes } from './views/Recipes.js';
import { Ingredients } from './views/Ingredients.js';
import { Shopping } from './views/Shopping.js';
import { Body } from './views/Body.js';
import { Settings } from './views/Settings.js';
import { SetupScreen } from './views/SetupScreen.js';
import { LoginScreen } from './views/LoginScreen.js';
import { PasswordResetModal } from './views/PasswordResetModal.js';
import { DatabaseCorruptScreen } from './views/DatabaseCorruptScreen.js';
import { AuthProvider, useAuth } from './auth/AuthContext.js';
import { AUTH_LOADING } from './auth/types.js';
import { ToastProvider } from './hooks/useToast.js';
import type { TabId, NavTab } from './types.js';

const SIDE_MENU_TABS: ReadonlyArray<NavTab> = [
  { id: 'dashboard', label: 'Dashboard', icon: '🏠' },
  { id: 'calendar', label: 'Calendar', icon: '📅' },
  { id: 'planner', label: 'Planner', icon: '🎯' },
  { id: 'recipes', label: 'Recipes', icon: '📖' },
  { id: 'ingredients', label: 'Ingredients', icon: '🥕' },
  { id: 'shopping', label: 'Shopping', icon: '🛒' },
  { id: 'body', label: 'Body', icon: '📈' },
  { id: 'settings', label: 'Settings', icon: '⚙️' },
];

function ActiveView({
  tab,
  onOpenRecipe,
  onNavigate,
}: {
  tab: TabId;
  onOpenRecipe: (id: number) => void;
  onNavigate: (tab: TabId) => void;
}): ReactElement {
  switch (tab) {
    case 'dashboard':
      return <Dashboard />;
    case 'calendar':
      return <Calendar onOpenRecipe={onOpenRecipe} />;
    case 'planner':
      return <Planner onNavigate={onNavigate} />;
    case 'recipes':
      // Recipes is mounted at the AppShell level to support the
      // Calendar → Recipes deep-link (`pendingRecipeId` plumbing).
      return <></>;
    case 'ingredients':
      return <Ingredients />;
    case 'shopping':
      return <Shopping />;
    case 'body':
      return <Body />;
    case 'settings':
      return <Settings />;
    default: {
      const exhaustive: never = tab;
      throw new Error(`Unknown tab: ${String(exhaustive)}`);
    }
  }
}

export function AppShell(): ReactElement {
  const [tab, setTab] = useState<TabId>('dashboard');
  const [pendingRecipeId, setPendingRecipeId] = useState<number | null>(null);
  const [menuOpen, setMenuOpen] = useState<boolean>(false);
  const { status } = useAuth();
  const mustReset =
    typeof status !== 'string' &&
    status.authenticated === true &&
    status.mustResetPassword === true;

  function openRecipe(id: number): void {
    setPendingRecipeId(id);
    setTab('recipes');
  }

  return (
    <div className="flex h-dvh flex-col">
      <Header onNavigate={setTab} onOpenMenu={() => setMenuOpen(true)} />
      <main className="flex-1 overflow-y-auto" data-testid="active-view">
        {tab === 'recipes' ? (
          <Recipes
            initialDetailId={pendingRecipeId}
            onConsumed={() => setPendingRecipeId(null)}
          />
        ) : (
          <ActiveView tab={tab} onOpenRecipe={openRecipe} onNavigate={setTab} />
        )}
      </main>
      <SideMenu
        open={menuOpen}
        tabs={SIDE_MENU_TABS}
        active={tab}
        onNavigate={setTab}
        onClose={() => setMenuOpen(false)}
      />
      {mustReset ? <PasswordResetModal /> : null}
    </div>
  );
}

function Router(): ReactElement {
  const { status } = useAuth();
  if (status === AUTH_LOADING) {
    return (
      <main
        className="flex min-h-dvh items-center justify-center"
        style={{
          background: 'var(--bg-primary)',
          color: 'var(--text-muted)',
        }}
        data-testid="auth-loading"
      >
        Loading…
      </main>
    );
  }
  if (status.databaseCorrupt) return <DatabaseCorruptScreen />;
  if (status.setupRequired) return <SetupScreen />;
  if (!status.authenticated) return <LoginScreen />;
  return <AppShell />;
}

function AppRoot(): ReactElement {
  return (
    <AuthProvider>
      <ToastProvider>
        <Router />
      </ToastProvider>
    </AuthProvider>
  );
}

export function App(): ReactElement {
  return <AppRoot />;
}